import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/cinch/EmptyState";
import { FlowGraph, type FlowGraphNode } from "@/components/cinch/FlowGraph";
import { KpiTile } from "@/components/cinch/KpiTile";

import { fmtMoney, fmtQty, monthLabel, useCosPeriod, useFlowMap } from "../api";

/**
 * The month as a knowledge graph: vendors on the left, their invoice charges
 * in the middle, the product lines those charges fund on the right. Edge
 * thickness is this month's recognized dollars; dashed edges are charges the
 * engine apportions at compute time (the Twilio support/tax split); muted
 * edges flow to the excluded sink.
 */
export function FlowMapView({ period }: { period: string }) {
  const { data: detail } = useCosPeriod(period);
  const exists = detail?.exists ?? false;
  const flow = useFlowMap(period, exists);
  const [selected, setSelected] = useState<string | null>(null);

  const graph = flow.data;
  const counts = useMemo(() => {
    const by = { vendor: 0, charge: 0, line: 0 };
    for (const n of graph?.nodes ?? []) by[n.column] += 1;
    return by;
  }, [graph]);

  const selectedNode: FlowGraphNode | null = useMemo(() => {
    if (!selected || !graph) return null;
    const n = graph.nodes.find((x) => x.id === selected);
    return n
      ? { id: n.id, column: n.column, label: n.label, value: n.amount, tone: undefined }
      : null;
  }, [selected, graph]);

  if (!exists) {
    return (
      <Card className="p-5 text-sm text-muted-foreground">
        {monthLabel(period)} hasn&apos;t been opened yet — upload its cost files first.
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <KpiTile label="Vendors billing" value={fmtQty(counts.vendor)} />
        <KpiTile label="Recurring charges" value={fmtQty(counts.charge)} />
        <KpiTile label="Product lines funded" value={fmtQty(counts.line)} />
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">
            Where {monthLabel(period)}&apos;s costs flow
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <svg width="26" height="8"><path d="M1 4 H25" strokeWidth="3" strokeLinecap="round" className="stroke-brand opacity-40" /></svg>
              recognized $
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg width="26" height="8"><path d="M1 4 H25" strokeWidth="2.5" strokeDasharray="5 3" strokeLinecap="round" className="stroke-brand opacity-40" /></svg>
              split at compute time
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg width="26" height="8"><path d="M1 4 H25" strokeWidth="2.5" strokeLinecap="round" className="stroke-muted-foreground opacity-40" /></svg>
              excluded
            </span>
          </div>
        </div>
        {flow.isLoading ? (
          <div className="h-[300px] animate-pulse rounded-md bg-muted/40" />
        ) : (graph?.nodes.length ?? 0) === 0 ? (
          <EmptyState title="Nothing to map yet">
            Upload and classify this month&apos;s cost files to see the flow.
          </EmptyState>
        ) : (
          <FlowGraph
            columns={[
              { key: "vendor", title: "Vendor" },
              { key: "charge", title: "Invoice charge" },
              { key: "line", title: "Product line" },
            ]}
            nodes={(graph?.nodes ?? []).map((n) => ({
              id: n.id,
              column: n.column,
              label: n.label,
              value: n.amount,
              tone:
                n.tone === "excluded" ? "muted" : n.tone === "unmapped" ? "attention" : "default",
            }))}
            links={(graph?.links ?? []).map((l) => ({
              source: l.source,
              target: l.target,
              value: l.amount,
              dashed: l.split,
              muted: l.excluded,
            }))}
            formatValue={(v) => fmtMoney(v) ?? String(v)}
            selectedId={selected}
            onNodeClick={(n) => setSelected(n.id === selected ? null : n.id)}
          />
        )}
        {selectedNode && (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary">{selectedNode.label}</Badge>
            highlighted — click it again to clear.
          </div>
        )}
      </Card>
    </div>
  );
}
